/**
 * PIPEDA-compliant cookie consent banner.
 *
 * Behaviour:
 *   • Renders on every route until `_ac_consent` is set.
 *   • Three primary actions: Accept All, Essentials Only, Customize.
 *   • Essential cookies (auth, OTP trust, lead-capture flags) are
 *     explicitly listed in the disclosure and ALWAYS work regardless
 *     of the user's choice — they're required for the site to
 *     function.
 *   • On accept, calls `bootAttribution()` so the visitor cookie is
 *     minted within the same click handler (no race).
 *   • Choice persists for 365 days; re-shown if the user clears
 *     cookies or revokes consent from /legal/cookie-policy.
 *
 * A11y: announced via `role="dialog"` + `aria-modal`, focus trap on
 * the primary action, ESC dismisses to "Essentials Only" to avoid
 * dark-pattern accusations.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Cookie, X } from "lucide-react";
import { hasConsent, setConsent, bootAttribution } from "../utils/attribution";
import { loadGtm } from "../lib/loadGtm";

export default function ConsentBanner() {
  // `null` = haven't checked yet (prevents a flash); false = need to ask;
  // true = already answered, banner stays hidden.
  const [decided, setDecided] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Defer the cookie read until after hydration so SSR/CSR match.
    // Additionally delay the banner appearance by 1.5s so the visitor
    // sees the hero first (UX-audit 2026-05-30 fix). Vision-LLM auditors
    // and real users were both reading the banner as a "modal overlay"
    // when it appeared immediately on first paint — the small delay
    // lets the page settle into focus first.
    if (hasConsent() || document.cookie.includes("_ac_consent=")) {
      setDecided(true);
      return;
    }
    const timer = setTimeout(() => setDecided(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Listen for the global "open cookie preferences" event so the footer
  // link (and anywhere else in the app) can re-surface the banner after
  // it's been dismissed. Resets to the "undecided" state so the visitor
  // can change their analytics opt-in. UX/compliance win 2026-05-30.
  useEffect(() => {
    const handler = () => setDecided(false);
    window.addEventListener("ac:open-cookie-preferences", handler);
    return () => window.removeEventListener("ac:open-cookie-preferences", handler);
  }, []);

  // Publish a `--ac-banner-offset` CSS variable on <html> while the
  // banner is mounted so floating widgets (chat bubble, husky mascot,
  // mobile call FAB) can lift above it instead of overlapping. Cleared
  // the moment the user chooses, so widgets settle back to their
  // resting positions without a layout flash.
  useEffect(() => {
    const root = document.documentElement;
    if (decided === false) {
      // ~190px on phones (banner is taller), ~150px on tablet/desktop
      const onResize = () => {
        const w = window.innerWidth;
        root.style.setProperty("--ac-banner-offset", w < 768 ? "200px" : w < 1024 ? "160px" : "0px");
      };
      onResize();
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
        root.style.removeProperty("--ac-banner-offset");
      };
    }
    root.style.removeProperty("--ac-banner-offset");
  }, [decided]);

  const choose = async (value) => {
    setConsent(value);
    setDecided(true);
    if (value === "accepted") {
      // Mint visitor/session cookies + /init POST in the same tick so
      // the user's first interaction is already attributed.
      await bootAttribution();
      // Load GTM in the same click handler. loadGtm() is idempotent,
      // admin-excluded, and consent-gated. Any dataLayer pushes queued
      // before consent (e.g. an early lead-form submit) will be delivered
      // by GTM once gtm.js parses.
      loadGtm();
    }
  };

  // Hide banner while we're still figuring out the saved choice — and
  // hide it once the user has answered. We re-render via decided state.
  if (decided !== false) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ac-cookie-banner-title"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 z-[80] mx-auto max-w-[420px] md:inset-x-auto md:left-6 md:bottom-6 md:max-w-[420px] ac-fade-up"
    >
      <div
        className="rounded-2xl bg-white border border-[var(--ac-line,#e6e8ee)] shadow-[0_30px_80px_-20px_rgba(15,30,60,0.35)] p-5 md:p-6"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex w-9 h-9 rounded-full bg-[#FFF6E7] text-[#A4761A] items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <h2
              id="ac-cookie-banner-title"
              className="text-[15px] font-bold uppercase tracking-[0.12em] text-[var(--ac-royal,#1F3A68)]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              We respect your privacy
            </h2>
            <p className="mt-1.5 text-[14px] md:text-[13.5px] text-[#3F4756] leading-relaxed">
              We use first-party cookies to understand how visitors find our
              programs and to keep your form sessions secure. Essential
              cookies are always on; analytics are optional.
            </p>
            <p className="mt-2.5 text-[13px] flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <Link
                to="/legal/cookie-policy"
                className="inline-block py-1 text-[var(--ac-royal,#1F3A68)] underline underline-offset-2 hover:text-[var(--ac-red,#A21D23)]"
                data-testid="cookie-banner-policy-link"
              >
                Read our Cookie Policy
              </Link>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="inline-block py-1 text-[var(--ac-royal,#1F3A68)] underline underline-offset-2 hover:text-[var(--ac-red,#A21D23)]"
                data-testid="cookie-banner-details-toggle"
              >
                {showDetails ? "Hide details" : "What we store"}
              </button>
            </p>
            {showDetails && (
              <ul className="mt-3 space-y-2 text-[13px] text-[#3F4756] border-l-2 border-[#e6e8ee] pl-3" data-testid="cookie-banner-details">
                <li><strong className="text-[var(--ac-ink,#0B1B36)]">Essential</strong> — OTP trust token, form session, error logging. Always on.</li>
                <li><strong className="text-[var(--ac-ink,#0B1B36)]">Analytics</strong> — anonymous visitor ID (365 d) + 30-min session ID. Helps us measure which programs prospects find most useful. No selling, no third-party sharing.</li>
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={() => choose("essential")}
            className="-mt-2 -mr-2 inline-flex items-center justify-center w-11 h-11 rounded-full text-[var(--ac-titanium,#5b6675)] hover:text-[var(--ac-ink,#0B1B36)] hover:bg-[#f5f6f8] transition"
            aria-label="Use essential cookies only"
            data-testid="cookie-banner-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2.5">
          <button
            type="button"
            onClick={() => choose("essential")}
            className="min-h-[44px] px-4 py-2.5 rounded-md border-2 border-[var(--ac-royal,#1F3A68)] text-[12.5px] font-bold uppercase tracking-[0.1em] text-[var(--ac-royal,#1F3A68)] hover:bg-[var(--ac-royal,#1F3A68)] hover:text-white transition"
            data-testid="cookie-banner-essential"
          >
            Essentials only
          </button>
          <button
            type="button"
            onClick={() => choose("accepted")}
            className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-[var(--ac-red,#A21D23)] text-white text-[12.5px] font-bold uppercase tracking-[0.12em] hover:bg-[var(--ac-royal,#1F3A68)] transition"
            data-testid="cookie-banner-accept"
          >
            <ShieldCheck className="w-4 h-4" /> Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
